// ContactTodesfallBelege.tsx — die Belege zum Todesfall eines Kontakts (T-357).
//
// Löst das Einzelfeld „Sterbeanzeige“ (ContactSterbeanzeigeFeld) ab. Dort
// ersetzte jeder zweite Upload den ersten — an einer Akte ging so die
// Sterbeurkunde verloren, als die Danksagung hochgeladen wurde. Jetzt:
//   - eine LISTE, eine Zeile je Datei, die Art je Datei wählbar
//     (Sterbeanzeige, Sterbeurkunde, Kondolenzschreiben, Danksagung,
//     Erbschein/Testamentseröffnung, Sonstiges mit Freitext);
//   - „Beleg hinzufügen“ hängt an, ersetzt nie (mehrere Dateien auf einmal);
//   - Entfernen nur nach einer eigenen Rückfrage — ein Tipp auf dem Handy
//     soll nicht wieder einen Beleg kosten. Auch dann ist es serverseitig nur
//     ein Soft-Delete, die Datei bleibt im Storage.
//
// Wie bisher rendert die Komponente nur den Inhalt, ohne Beschriftung und
// ohne Feld-Hülle: Sie steht in zwei Masken mit verschiedenen Designsystemen
// (Akte in „kvp-rd“, Anlage-Formular in Fluent). Die Hülle stellt der Aufrufer.
//
// Der Upload geht SOFORT raus (die Datei liegt danach im Storage), die
// VERKNÜPFUNG mit dem Kontakt erst beim Speichern bzw. Anlegen.

import * as React from "react";

import { apiUrl } from "../../utils/api";
import { bestaetigen } from "./bestaetigen";
import {
  TODESFALL_BELEG_ARTEN,
  todesfallBelegArtLabel,
  type TodesfallBeleg,
  type TodesfallBelegArt,
} from "../../data/todesfallBelege";

const UPLOAD_ICON = (
  <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 12.5V3.5M6.5 7L10 3.5 13.5 7M4 15.5h12" />
  </svg>
);

const DOKUMENT_ICON = (
  <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
    <path d="M5.5 2.5h6L15 6v11.5H5.5z" /><path d="M11.5 2.5V6H15" />
  </svg>
);

const ENTFERNEN_ICON = (
  <svg width="14" height="14" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round">
    <path d="M4.5 4.5l9 9M13.5 4.5l-9 9" />
  </svg>
);

/**
 * Öffnet eine geschützte API-Ressource in einem neuen Tab.
 *
 * `window.open(url)` liefe nicht durch den fetch-Wrapper, der den
 * Bearer-Token anhängt — `/media/sterbeanzeigen/:file` antwortete mit 401.
 * Deshalb der Blob-Weg wie bei Vollmachten und Organigramm. (Bewusst nicht
 * `useOpenDocument`: das zöge pdfjs in die jsdom-Tests.)
 */
async function oeffneAuthentifiziert(pfad: string): Promise<void> {
  const res = await fetch(apiUrl(pfad));
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const geoeffnet = window.open(objectUrl, "_blank", "noopener,noreferrer");
  if (geoeffnet) window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

/** Anzeigename einer Zeile: Dateiname, sonst „Hinterlegt“ (Altbestand ohne Namen). */
export function belegDateiText(b: Pick<TodesfallBeleg, "name">): string {
  const n = String(b.name ?? "").trim();
  return n || "Hinterlegt";
}

/** Art-Beschriftung einer Zeile, bei „Sonstiges“ mit Freitext. */
export function belegArtText(b: Pick<TodesfallBeleg, "art" | "bezeichnung">): string {
  const label = todesfallBelegArtLabel(b.art);
  const frei = String(b.bezeichnung ?? "").trim();
  return b.art === "SONSTIGES" && frei ? `${label}: ${frei}` : label;
}

export interface ContactTodesfallBelegeProps {
  /** Die Belege (`Contact.todesfallBelege`). */
  value: TodesfallBeleg[];
  /** Schreibrecht. Ohne das bleibt nur das Ansehen. */
  editable?: boolean;
  /** Neuer Stand — landet im Formularzustand, geschrieben wird mit „Speichern“. */
  onChange?: (next: TodesfallBeleg[]) => void;
  /** Fehlermeldung an den Aufrufer (Toast/Popup). */
  onFehler?: (meldung: string) => void;
  /** Eindeutige Id für das versteckte Datei-Feld (mehrere Masken auf einer Seite). */
  inputId?: string;
}

export function ContactTodesfallBelege({
  value,
  editable,
  onChange,
  onFehler,
  inputId = "todesfall-belege-datei",
}: ContactTodesfallBelegeProps) {
  const belege = Array.isArray(value) ? value : [];
  const [laedtHoch, setLaedtHoch] = React.useState(false);
  // Die Art, mit der die nächste Datei angelegt wird. Ohne Bestand ist es fast
  // immer die Sterbeanzeige; danach muss bewusst gewählt werden.
  const [neueArt, setNeueArt] = React.useState<TodesfallBelegArt>(
    belege.length ? "SONSTIGES" : "STERBEANZEIGE",
  );
  const dateiRef = React.useRef<HTMLInputElement>(null);

  const melde = (meldung: string, fehler: unknown) => {
    console.error(meldung, fehler);
    onFehler?.(meldung);
  };

  const oeffne = (b: TodesfallBeleg) => {
    void oeffneAuthentifiziert(b.url.replace(/^\/+/, "")).catch((e) =>
      melde("Beleg konnte nicht geöffnet werden.", e),
    );
  };

  const aendern = (index: number, patch: Partial<TodesfallBeleg>) => {
    onChange?.(belege.map((b, i) => (i === index ? { ...b, ...patch } : b)));
  };

  const entfernen = async (index: number) => {
    const b = belege[index];
    if (!b) return;
    const ok = await bestaetigen({
      titel: "Beleg entfernen?",
      text: `„${belegDateiText(b)}“ (${belegArtText(b)}) wird aus der Akte entfernt.\nWirksam erst mit „Speichern“.`,
      ja: "Entfernen",
      ton: "danger",
    });
    if (!ok) return;
    onChange?.(belege.filter((_, i) => i !== index));
  };

  const nimmDateien = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const dateien = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (!dateien.length) return;
    setLaedtHoch(true);
    const neu: TodesfallBeleg[] = [];
    try {
      for (const datei of dateien) {
        try {
          const fd = new FormData();
          fd.append("file", datei);
          const res = await fetch(apiUrl("upload/contact-sterbeanzeige"), { method: "POST", body: fd });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const { url, name } = (await res.json()) as { url: string; name?: string };
          neu.push({ art: neueArt, bezeichnung: null, url, name: name ?? datei.name ?? null });
        } catch (err) {
          melde(`„${datei.name}“ konnte nicht hochgeladen werden.`, err);
        }
      }
    } finally {
      setLaedtHoch(false);
    }
    // Anhängen, nie ersetzen — der Kern von T-357.
    if (neu.length) onChange?.([...belege, ...neu]);
  };

  if (!editable && belege.length === 0) {
    return <span className="sza-leer">Nicht hinterlegt</span>;
  }

  return (
    <div className="sza-liste">
      {belege.length > 0 && (
        <ul className="sza-belege" aria-label="Belege zum Todesfall">
          {belege.map((b, i) => (
            <li key={`${b.id ?? "neu"}-${b.url}-${i}`} className="sza-beleg">
              {editable ? (
                <select
                  className="sza-art"
                  aria-label={`Art des Belegs ${belegDateiText(b)}`}
                  value={b.art}
                  onChange={(ev) =>
                    aendern(i, {
                      art: ev.target.value as TodesfallBelegArt,
                      ...(ev.target.value === "SONSTIGES" ? {} : { bezeichnung: null }),
                    })
                  }
                >
                  {TODESFALL_BELEG_ARTEN.map((a) => (
                    <option key={a.code} value={a.code}>{a.label}</option>
                  ))}
                </select>
              ) : (
                <span className="sza-art-text">{belegArtText(b)}</span>
              )}
              <button
                type="button"
                className="sza-btn sza-datei"
                onClick={() => oeffne(b)}
                title={`Beleg öffnen: ${belegDateiText(b)}`}
              >
                {DOKUMENT_ICON}
                <span className="sza-dateiname">{belegDateiText(b)}</span>
              </button>
              {editable && (
                <button
                  type="button"
                  className="sza-btn sza-entfernen"
                  onClick={() => void entfernen(i)}
                  title={`Beleg „${belegDateiText(b)}“ entfernen`}
                  aria-label={`Beleg „${belegDateiText(b)}“ entfernen`}
                >
                  {ENTFERNEN_ICON}
                </button>
              )}
              {editable && b.art === "SONSTIGES" && (
                <input
                  type="text"
                  className="sza-bezeichnung"
                  aria-label={`Bezeichnung des Belegs ${belegDateiText(b)}`}
                  placeholder="Was ist das für ein Beleg?"
                  maxLength={255}
                  value={b.bezeichnung ?? ""}
                  onChange={(ev) => aendern(i, { bezeichnung: ev.target.value })}
                />
              )}
            </li>
          ))}
        </ul>
      )}

      {editable && (
        <div className="sza-zeile sza-neu">
          <span className="sza-art-text" aria-hidden="true">Neuer Beleg:</span>
          <select
            className="sza-art"
            aria-label="Art des neuen Belegs"
            value={neueArt}
            onChange={(ev) => setNeueArt(ev.target.value as TodesfallBelegArt)}
            disabled={laedtHoch}
          >
            {TODESFALL_BELEG_ARTEN.map((a) => (
              <option key={a.code} value={a.code}>{a.label}</option>
            ))}
          </select>
          <button
            type="button"
            className="sza-btn"
            disabled={laedtHoch}
            onClick={() => dateiRef.current?.click()}
          >
            {UPLOAD_ICON}
            {laedtHoch ? "Lädt hoch …" : "Beleg hinzufügen"}
          </button>
          <input
            ref={dateiRef}
            id={inputId}
            type="file"
            multiple
            // Bild ODER PDF — beides ist hier der Normalfall.
            accept="application/pdf,image/*"
            style={{ display: "none" }}
            aria-label="Beleg hochladen"
            onChange={(ev) => void nimmDateien(ev)}
          />
        </div>
      )}
    </div>
  );
}
